import { useMemo, useState, type ReactNode } from 'react';
import { formatCost, totalRunCost } from './promptUtils';
import './operation-detail.css';

/**
 * "Prompts Executed" table — one row per model run for this operation.
 *
 * Generic over the row type so each operation can pass its own shape
 * (hint: one row; submission: N rows). Columns are fully configurable.
 *
 * See docs/FeatureDesigns/operation-detail-unified-layout.md.
 */
export interface RunsTableColumn<T> {
    header: string;
    render: (row: T) => ReactNode;
    className?: string;
    /** Supplying this makes the column sortable — the header becomes a control
     *  and this is what it sorts on. Sort on the underlying VALUE, not the
     *  rendered text: the Time cell shows mm:ss, which reorders wrongly across
     *  an hour boundary. Return null for "no value"; those rows sort last in
     *  both directions rather than clumping at whichever end nulls compare to. */
    sortValue?: (row: T) => number | string | null | undefined;
}

export type SortDirection = 'asc' | 'desc';

/** A column identified by its header, plus which way it is sorted. */
export interface RunsTableSort {
    column: string;
    direction: SortDirection;
}

/** Chronological by default. A runs table is a record of an execution, and
 *  every page that renders one leads with the Time column; ordering by
 *  anything else has to be asked for. Tables with no Time column are simply
 *  left in the order the page passed them. */
const DEFAULT_SORT: RunsTableSort = { column: 'Time', direction: 'asc' };

/** Nulls last in both directions; numbers numerically; everything else as text. */
function compareValues(a: unknown, b: unknown): number {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b));
}

export interface RunsTableProps<T> {
    rows: T[];
    columns: RunsTableColumn<T>[];
    /** Key extractor for each row. */
    rowKey: (row: T) => string;
    /** When provided, rows become clickable and the given id is highlighted. */
    selectedRowId?: string | null;
    onSelect?: (row: T) => void;
    /** Extra CSS class applied to each row. Useful for primary / error states. */
    getRowClass?: (row: T) => string | undefined;
    /** Card title — defaults to "Prompts Executed (N)". */
    title?: string;
    /** Which column the table sorts by before the user touches a header.
     *  Defaults to Time ascending; pass null to keep the caller's own order. */
    initialSort?: RunsTableSort | null;
    /** The cost of one row. Defaults to the row's own `cost`, which every
     *  model-output row carries; the header shows the sum of them all. */
    costOf?: (row: T) => number | null | undefined;
}

export function RunsTable<T>({
    rows,
    columns,
    rowKey,
    selectedRowId,
    onSelect,
    getRowClass,
    title,
    initialSort = DEFAULT_SORT,
    costOf,
}: RunsTableProps<T>) {
    const cardTitle = title ?? `Prompts Executed (${rows.length})`;
    // The operation's whole cost: every call it made, in the one place every
    // operation page shows its calls.
    const total = totalRunCost(rows.map(r => ({
        cost: costOf ? costOf(r) : (r as { cost?: unknown }).cost as number | undefined,
    })));
    const [sort, setSort] = useState<RunsTableSort | null>(initialSort);

    // A sort naming a column this table doesn't have (or one that isn't
    // sortable) is ignored rather than emptying the table — the default names
    // 'Time', which not every caller renders.
    const sortColumn = sort && columns.find(c => c.header === sort.column && c.sortValue);
    const sortedRows = useMemo(() => {
        if (!sortColumn || !sort) return rows;
        const sign = sort.direction === 'desc' ? -1 : 1;
        // Array.sort is stable, so rows that tie keep the caller's order.
        return [...rows].sort((a, b) => {
            const av = sortColumn.sortValue!(a);
            const bv = sortColumn.sortValue!(b);
            // Nulls are already forced last by compareValues, so the direction
            // sign must not flip them back to the top.
            if (av == null || bv == null) return compareValues(av, bv);
            return sign * compareValues(av, bv);
        });
    }, [rows, sortColumn, sort]);

    const toggleSort = (col: RunsTableColumn<T>) => {
        setSort(prev => prev?.column === col.header
            ? { column: col.header, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
            : { column: col.header, direction: 'asc' });
    };

    return (
        <div className="card experiments-card">
            <h3 className="runs-table-title">
                {cardTitle}
                {total !== undefined && (
                    <span className="runs-table-total" title="Every model call of this operation, failed hops and escalations included">
                        Total cost {formatCost(total)}
                    </span>
                )}
            </h3>
            <div className="experiments-table-wrapper">
                <table className="experiments-table">
                    <thead>
                        <tr>
                            {columns.map(col => {
                                if (!col.sortValue) return <th key={col.header}>{col.header}</th>;
                                const active = sort?.column === col.header;
                                return (
                                    <th
                                        key={col.header}
                                        className={`is-sortable${active ? ' is-sorted' : ''}`}
                                        aria-sort={active ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                                        title={`Sort by ${col.header}`}
                                        onClick={() => toggleSort(col)}
                                    >
                                        {col.header}
                                        <span className="sort-arrow" aria-hidden>
                                            {active ? (sort!.direction === 'asc' ? '▲' : '▼') : '↕'}
                                        </span>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedRows.map(row => {
                            const id = rowKey(row);
                            const extraClass = getRowClass?.(row) || '';
                            const selectedClass = selectedRowId === id ? 'selected' : '';
                            return (
                                <tr
                                    key={id}
                                    className={`experiment-row ${extraClass} ${selectedClass}`.trim()}
                                    onClick={onSelect ? () => onSelect(row) : undefined}
                                >
                                    {columns.map(col => (
                                        <td key={col.header} className={col.className}>
                                            {col.render(row)}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
